import React, { useState, useEffect, useRef, useMemo } from "react";
import { Typography } from "@mhome/ui";
import NumberUnitInput from "../input/number-unit-input";

// Unit mappings for different data types
const UNIT_MAPPINGS = {
  Duration: {
    units: ["sec", "min", "hour", "day"],
    steps: [1, 1, 1, 1],
    defaultUnit: "min",
    displayUnits: ["sec", "min", "hour", "day"], // For display
  },
  Temperature: {
    units: ["C", "F"],
    steps: [0.5, 1],
    defaultUnit: "C",
    displayUnits: ["°C", "°F"],
  },
  TemperatureDiff: {
    units: ["dC", "dF"],
    steps: [0.5, 1],
    defaultUnit: "dC",
    displayUnits: ["Δ°C", "Δ°F"],
  },
  Length: {
    units: ["mm", "cm", "m", "km", "in", "ft"],
    steps: [1, 1, 1, 1, 0.1, 0.1],
    defaultUnit: "m",
    displayUnits: ["mm", "cm", "m", "km", "in", "ft"],
  },
  Weight: {
    units: ["g", "kg", "lb", "oz"],
    steps: [1, 1, 0.1, 0.1],
    defaultUnit: "kg",
    displayUnits: ["g", "kg", "lb", "oz"], // For display
  },
};

const NumberUnitCell = ({
  value,
  onChange,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  meta = {},
  ...cellProps
}) => {
  const [editValue, setEditValue] = useState(value || "");
  const editContainerRef = useRef(null);

  const { dataType, min, max } = meta;
  const unitConfig = UNIT_MAPPINGS[dataType] || UNIT_MAPPINGS.Duration;

  // Focus the edit container when editing starts
  useEffect(() => {
    if (isEditing && editContainerRef.current) {
      editContainerRef.current.focus();
    }
  }, [isEditing]);

  const handleCellClick = () => {
    if (!isEditing) {
      // Parse current value or set default
      const currentValue = value || `0${unitConfig.defaultUnit}`;
      setEditValue(currentValue);
      onEdit();
    }
  };

  const handleNumberUnitChange = (newValue, newUnit) => {
    const combinedValue = `${newValue}${newUnit}`;
    setEditValue(combinedValue);
    onChange(combinedValue);
    onSave();
  };

  // Parse current value to extract number and unit
  const parseValue = (val) => {
    if (!val) return { number: 0, unit: unitConfig.defaultUnit };

    // Handle values like "20C", "68F", "20°C", "68°F"
    const match = val.match(/^([+-]?[0-9.]+)(°?[A-Za-z]+)$/);
    if (match) {
      const [, numStr, unitStr] = match;
      // Remove degree symbol if present and normalize unit
      let unit = unitStr.replace("°", "");
      return {
        number: parseFloat(numStr),
        unit: unit,
      };
    }

    // Fallback: treat as number with default unit
    const numValue = parseFloat(val);
    return {
      number: isNaN(numValue) ? 0 : numValue,
      unit: unitConfig.defaultUnit,
    };
  };

  // Create valueSpec for NumberUnitInput
  const createValueSpec = () => {
    const currentValue = editValue || value || `0${unitConfig.defaultUnit}`;
    const parsed = parseValue(currentValue);

    return {
      dataType: dataType,
      value: currentValue,
      number: parsed.number,
      unit: parsed.unit,
      units: unitConfig.units,
      steps: unitConfig.steps,
      stepUnits: unitConfig.units,
      min: min || 0,
      max: max || null,
      readOnly: false,
    };
  };

  // Format display value
  const getDisplayValue = () => {
    const displayVal = value || `0${unitConfig.defaultUnit}`;

    // Parse the value to show number and unit separately
    const match = displayVal.match(/^([+-]?[0-9.]+)(°?[A-Za-z]+)$/);
    if (match) {
      const [, num, unit] = match;
      const cleanUnit = unit.replace("°", "");

      // Find the display unit for this internal unit
      const unitIndex = unitConfig.units.indexOf(cleanUnit);
      const displayUnit =
        unitIndex >= 0 && unitConfig.displayUnits
          ? unitConfig.displayUnits[unitIndex]
          : unit;

      return `${parseFloat(num)} ${displayUnit}`;
    }
    return displayVal;
  };

  // Convert MUI spacing to pixels (MUI spacing unit = 8px)
  const spacingToPx = (spacing) =>
    typeof spacing === "number" ? spacing * 8 : spacing;

  const cellStyle = useMemo(
    () => ({
      cursor: "pointer",
      padding: "4px",
      border: isEditing ? `2px solid hsl(var(--primary))` : "none",
      borderRadius: isEditing ? spacingToPx(1) : 0, // 8px
      width: "100%",
      height: "40px",
      display: "flex",
      alignItems: "center",
    }),
    [isEditing]
  );

  return (
    <div
      style={cellStyle}
      onClick={handleCellClick}
      onMouseEnter={(e) => {
        if (!isEditing) {
          e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
      }}
    >
      {isEditing ? (
        <div
          ref={editContainerRef}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <NumberUnitInput
            valueSpec={createValueSpec()}
            onChange={handleNumberUnitChange}
            onError={() => {}}
            zIndex={1400}
          />
        </div>
      ) : (
        <Typography
          variant="body2"
          style={{
            paddingLeft: spacingToPx(1), // 8px
            paddingRight: spacingToPx(1), // 8px
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {getDisplayValue()}
        </Typography>
      )}
    </div>
  );
};

export default NumberUnitCell;
